import { BodyOf } from "../../core/api";
import { createMutationGroup } from "../../core/api/mutations";
import { AiWeeklyTemplateSuggestionId, AuthorizationId } from "../../shared/schema/schema";

export default createMutationGroup(({ api, createMutation }) => ({
  /**
   * Runs the model and the server-side verification. Expect 30-90 seconds.
   *
   * Deliberately has no `retry`: react-query does not retry mutations by
   * default, and this one costs real money and burns a slot from the patient's
   * daily quota, so it must never fire twice on its own.
   */
  generate: (authorizationId: AuthorizationId) =>
    createMutation({
      mutationFn: (
        body: BodyOf<
          "post",
          "./authorizations/:authorizationId/ai_weekly_template_builder/generate"
        >
      ) =>
        api.post("./authorizations/:authorizationId/ai_weekly_template_builder/generate", {
          path: { authorizationId },
          body,
        }),
    }),

  /**
   * Another model call: the coordinator's feedback on a suggestion, answered with
   * a revised one. Costs the same as `generate` — one of the patient's daily
   * slots — and takes about as long, so it gets the same no-retry treatment.
   */
  refine: (authorizationId: AuthorizationId, suggestionId: AiWeeklyTemplateSuggestionId) =>
    createMutation({
      mutationFn: (
        body: BodyOf<
          "post",
          "./authorizations/:authorizationId/ai_weekly_template_builder/suggestions/:suggestionId/refine"
        >
      ) =>
        api.post(
          "./authorizations/:authorizationId/ai_weekly_template_builder/suggestions/:suggestionId/refine",
          { path: { authorizationId, suggestionId }, body }
        ),
    }),

  /**
   * Creates the patient's weekly template and the visits generated from it.
   *
   * Like `generate`, deliberately without `retry`: this can create dozens of
   * visits, so it must never fire twice on its own. The server is idempotent per
   * suggestion as the real guard, but there is no reason to lean on that.
   */
  apply: (authorizationId: AuthorizationId, suggestionId: AiWeeklyTemplateSuggestionId) =>
    createMutation({
      mutationFn: (
        body: BodyOf<
          "post",
          "./authorizations/:authorizationId/ai_weekly_template_builder/suggestions/:suggestionId/apply"
        >
      ) =>
        api.post(
          "./authorizations/:authorizationId/ai_weekly_template_builder/suggestions/:suggestionId/apply",
          { path: { authorizationId, suggestionId }, body }
        ),
    }),
}));
